import React, { useState, useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import axios from "axios";
import phoneMockup from "../../../assets/images/contact_us_mock_up.png"; 

const API_BASE_URL = "https://api.beatsfussion.com/api";

const ContactUs: React.FC = () => {
  const [isMobileView, setIsMobileView] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    business_name: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false); 

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 430);
    handleResize();
    window.addEventListener("resize", handleResize);

    AOS.init({
      duration: 1000,
      easing: "ease-in-out",
      once: true,
    });

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (successMessage) {
      setShowSuccess(true); 
      const timer = setTimeout(() => {
        setShowSuccess(false); 
        setSuccessMessage(""); 
      }, 5000); 

      return () => clearTimeout(timer); 
    }
  }, [successMessage]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      await axios.post(`${API_BASE_URL}/landing-page/create-leads/`, formData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      setSuccessMessage("Your message has been sent successfully!");
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        business_name: "",
        message: "",
      });
    } catch (error) {
      setErrorMessage("Failed to send message. Please try again.");
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      className="w-11/12 sm:w-9/12 max-w-7xl mt-16 sm:mt-28 mx-auto py-20 flex flex-col lg:flex-row items-center gap-12 bg-white rounded-2xl shadow-lg border border-gray-100 px-8 lg:px-16"
      data-aos="fade-up"
      data-aos-delay="100"
    >
      <div className="w-full lg:w-1/2" data-aos="fade-right" data-aos-delay="200">
        <h2 className="text-3xl lg:text-4xl font-medium text-black mb-8">We want to hear from you</h2>

        {errorMessage && <p className="text-red-500">{errorMessage}</p>}
        {showSuccess && (
          <p
            className="text-green-500 transition-opacity duration-500 ease-in-out"
            style={{ opacity: showSuccess ? 1 : 0 }}
          >
            {successMessage}
          </p>
        )}

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">First name</label>
            <input
              id="first_name"
              name="first_name"
              type="text"
              value={formData.first_name}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 focus:ring-purple-500 text-sm focus:border-purple-500"
              required
            />
          </div>
          <div>
            <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">Last name</label>
            <input
              id="last_name"
              name="last_name"
              type="text"
              value={formData.last_name}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 focus:ring-purple-500 text-sm focus:border-purple-500"
              required
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 focus:ring-purple-500 text-sm focus:border-purple-500"
              required
            />
          </div>
          <div>
            <label htmlFor="business_name" className="block text-sm font-medium text-gray-700">Business name</label>
            <input
              id="business_name"
              name="business_name"
              type="text"
              value={formData.business_name}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 focus:ring-purple-500 text-sm focus:border-purple-500"
              required
            />
          </div>
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700">Drop us your message</label>
            <textarea
              id="message"
              name="message"
              rows={4}
              value={formData.message}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 focus:ring-purple-500 text-sm focus:border-purple-500"
              required
            />
          </div>
          <button
            type="submit"
            className="w-full lg:w-auto px-12 py-4 bg-purple-600 text-white font-medium rounded-xl shadow-md hover:bg-purple-700 transition disabled:opacity-50"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </form>
      </div>

      {!isMobileView && (
        <div className="w-full lg:w-1/2 hidden sm:flex justify-center" data-aos="fade-left" data-aos-delay="300">
          <img src={phoneMockup} alt="Phone Mockup" className="w-72 sm:w-80 lg:w-96" />
        </div>
      )}
    </section>
  );
};

export default ContactUs;